import React, { FC } from "react";
import classNames from "classnames/dedupe";
import { Button } from "../../ActionsSelectors/Button";
import { SvgIcon } from "../../Icons";
import { Typography } from "../../Fundamentals";
import { priceFormat } from "../../../helpers/priceFormat";

export interface IBundlesSummaryProps {
  /** Bundle name inserted in heading of component */
  bundleName: string;
  /** Custom styles */
  classes?: {
    root?: string;
  };
  /** If there is an additional fee it is concatinated in heading */
  bundleFee?: number;
  /** Locale of a user */
  locale?: string;
  /** If there is an additional fee currency prop should be added */
  currency?: string;
  /** Body of bundles summary */
  details?: { detailName: string; detailValue: string | number }[];
  /** Background color set to bundles summary component */
  background?: "grey" | "white";
  /** Enables "remove" button */
  withRemove?: boolean;
  /** Edit action callback */
  onEdit?: (event: React.MouseEvent) => void;
  /** Remove action callback */
  onRemove?: (event: React.MouseEvent) => void;
}

const BundlesSummary: FC<IBundlesSummaryProps> = ({
  classes,
  bundleName,
  bundleFee = 0,
  currency = "SEK",
  locale = "swe",
  details,
  background = "grey",
  withRemove = false,
  onEdit,
  onRemove,
}) => {
  const rootClasses = classNames(
    "py-7 px-6 d-flex justify-content-between",
    { "bg-color-grey-200": background === "grey" },
    { "bg-color-white": background === "white" },
    classes?.root,
  );

  const feeToAdd = priceFormat(bundleFee, currency, locale);

  const bundleTitle = `${bundleName} +${feeToAdd}`;

  return (
    <article className={rootClasses}>
      <div className="d-flex flex-column justify-content-center">
        <h4
          className="d-header-14-bold text-color-blue m-0"
          data-testid="bundles-summary-title"
        >
          {bundleTitle}
        </h4>
        {details && (
          <div
            className="d-flex flex-column gap-3 mt-4"
            data-testid="bundles-summary-details"
          >
            {details.map((detail) => (
              <Typography
                key={detail.detailName}
                variant="p-medium"
                classes={{ root: "text-color-blue" }}
                as="div"
              >
                {`${detail.detailName}: ${detail.detailValue}`}
              </Typography>
            ))}
          </div>
        )}
      </div>

      <div className="d-flex flex-column align-self-stretch justify-content-between">
        <Button
          data-testid="bundles-summary-edit"
          variant="iconSmall"
          classes={{
            root: "text-color-red-hover",
          }}
          onClick={onEdit}
        >
          <SvgIcon type="svg-icon" icon="EditIcon" />
        </Button>

        {withRemove && (
          <Button
            data-testid="bundles-summary-remove"
            variant="iconSmall"
            classes={{
              root: "text-color-red-hover",
            }}
            onClick={onRemove}
          >
            <SvgIcon type="svg-icon" icon="DeleteIcon" />
          </Button>
        )}
      </div>
    </article>
  );
};

export default BundlesSummary;
